import { PageTypeGlobal } from "@/lib/types";
import type { PublicBackLink } from "@/lib/utils/publicReturnToClass";

export type PublicListingKind = "classes" | "teachers" | "locations";

const LISTING_PATH: Record<PublicListingKind, string> = {
  classes: PageTypeGlobal.Classes,
  teachers: PageTypeGlobal.Teachers,
  locations: PageTypeGlobal.Locations,
};

const STORAGE_KEY: Record<PublicListingKind, string> = {
  classes: "gh_publicClassesListingHref",
  teachers: "gh_publicTeachersListingHref",
  locations: "gh_publicLocationsListingHref",
};

const BACK_LABEL: Record<PublicListingKind, string> = {
  classes: "Back to Classes",
  teachers: "Back to instructors",
  locations: "Back to locations",
};

export function getDefaultPublicListingBackLink(
  kind: PublicListingKind,
): PublicBackLink {
  return { href: LISTING_PATH[kind], label: BACK_LABEL[kind] };
}

/** Keep only listing path + query (never a detail slug). */
export function sanitizePublicListingHref(
  kind: PublicListingKind,
  href: string | null | undefined,
): string | null {
  if (!href) return null;

  try {
    const url = new URL(href, "https://example.invalid");
    if (url.pathname !== LISTING_PATH[kind]) return null;
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

export function persistPublicListingHref(
  kind: PublicListingKind,
  href: string,
): void {
  const sanitized = sanitizePublicListingHref(kind, href);
  if (!sanitized) return;

  try {
    sessionStorage.setItem(STORAGE_KEY[kind], sanitized);
  } catch {
    /* private mode */
  }
}

export function readPublicListingHref(kind: PublicListingKind): string | null {
  try {
    return sanitizePublicListingHref(
      kind,
      sessionStorage.getItem(STORAGE_KEY[kind]),
    );
  } catch {
    return null;
  }
}

export function parseListingHrefFromReferrer(
  kind: PublicListingKind,
): string | null {
  if (typeof document === "undefined") return null;

  try {
    const ref = document.referrer;
    if (!ref) return null;
    const url = new URL(ref);
    if (url.origin !== window.location.origin) return null;
    return sanitizePublicListingHref(kind, `${url.pathname}${url.search}`);
  } catch {
    return null;
  }
}

export function resolvePublicListingBackLink(
  kind: PublicListingKind,
): PublicBackLink {
  const fallback = getDefaultPublicListingBackLink(kind);
  const href =
    readPublicListingHref(kind) ?? parseListingHrefFromReferrer(kind);

  return href ? { href, label: fallback.label } : fallback;
}
